import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { TIERS } from "../core/config";
import { terrainHeightMeters } from "../core/TokyoTerrain";

const SKYTREE_WHITE = new THREE.Color(0xf7fbff);
const SKYTREE_SILVER = new THREE.Color(0xb8d2dd);
const SKYTREE_BLUE = new THREE.Color(0x62bce8);
const SKYTREE_DEEP_BLUE = new THREE.Color(0x277eaa);

function setVertexColor(
  geometry: THREE.BufferGeometry,
  color: THREE.Color,
): THREE.BufferGeometry {
  const vertexCount = geometry.getAttribute("position").count;
  const colors = new Float32Array(vertexCount * 3);
  for (let index = 0; index < vertexCount; index += 1) {
    const offset = index * 3;
    colors[offset] = color.r;
    colors[offset + 1] = color.g;
    colors[offset + 2] = color.b;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geometry;
}

function createLandmarkGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const add = (
    geometry: THREE.BufferGeometry,
    color: THREE.Color,
  ): void => {
    parts.push(setVertexColor(geometry, color));
  };

  // The normalized model is exactly 2.0 units high. With the gameplay
  // equivalent radius of 317 m, the existing radius-based scale therefore
  // renders Tokyo Skytree at its recognizable 634 m visual height.
  const foundation = new THREE.CylinderGeometry(0.34, 0.37, 0.06, 12, 1);
  foundation.translate(0, 0.03, 0);
  add(foundation, SKYTREE_DEEP_BLUE);

  const lowerTower = new THREE.CylinderGeometry(0.115, 0.31, 0.82, 3, 6);
  lowerTower.rotateY(Math.PI / 6);
  lowerTower.translate(0, 0.47, 0);
  add(lowerTower, SKYTREE_WHITE);

  const middleTower = new THREE.CylinderGeometry(0.065, 0.125, 0.56, 6, 4);
  middleTower.translate(0, 1.12, 0);
  add(middleTower, SKYTREE_SILVER);

  // Two observation decks create the characteristic stepped silhouette.
  const lowerDeck = new THREE.CylinderGeometry(0.175, 0.19, 0.075, 16, 1);
  lowerDeck.translate(0, 1.015, 0);
  add(lowerDeck, SKYTREE_DEEP_BLUE);
  const lowerDeckRing = new THREE.TorusGeometry(0.18, 0.014, 4, 20);
  lowerDeckRing.rotateX(Math.PI * 0.5);
  lowerDeckRing.translate(0, 1.04, 0);
  add(lowerDeckRing, SKYTREE_BLUE);

  const upperDeck = new THREE.CylinderGeometry(0.11, 0.13, 0.09, 16, 1);
  upperDeck.translate(0, 1.285, 0);
  add(upperDeck, SKYTREE_BLUE);
  const upperDeckRing = new THREE.TorusGeometry(0.115, 0.012, 4, 20);
  upperDeckRing.rotateX(Math.PI * 0.5);
  upperDeckRing.translate(0, 1.31, 0);
  add(upperDeckRing, SKYTREE_WHITE);

  const broadcastMast = new THREE.CylinderGeometry(0.018, 0.043, 0.66, 8, 3);
  broadcastMast.translate(0, 1.65, 0);
  add(broadcastMast, SKYTREE_WHITE);

  // Sparse diagonal braces read as the tower's steel lattice while remaining
  // part of the same merged geometry and submitted draw.
  for (let level = 0; level < 4; level += 1) {
    const y = 0.18 + level * 0.19;
    const halfWidth = 0.27 - level * 0.038;
    for (const side of [-1, 1] as const) {
      const brace = new THREE.BoxGeometry(0.018, 0.31, 0.018);
      brace.rotateZ(side * 0.56);
      brace.translate(side * halfWidth * 0.42, y + 0.14, 0);
      add(brace, level % 2 === 0 ? SKYTREE_BLUE : SKYTREE_SILVER);
    }
  }

  const exactTip = new THREE.ConeGeometry(0.02, 0.04, 8, 1);
  exactTip.translate(0, 1.98, 0);
  add(exactTip, SKYTREE_BLUE);

  const merged = mergeGeometries(parts, false);
  for (const part of parts) {
    part.dispose();
  }
  if (!merged) {
    throw new Error("Failed to merge the Tokyo Skytree geometry");
  }

  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  merged.name = "tokyo-skytree-634m-merged-geometry";
  return merged;
}

function smoothstep(value: number): number {
  const bounded = THREE.MathUtils.clamp(value, 0, 1);
  return bounded * bounded * (3 - 2 * bounded);
}

export class GoalLandmark {
  readonly group = new THREE.Group();
  readonly position = new THREE.Vector3();
  readonly equivalentRadiusMeters: number;

  spawned = false;
  absorbed = false;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly mesh: THREE.Mesh;
  private baseYaw = 0;
  private disposed = false;

  constructor(equivalentRadiusMeters: number) {
    if (!Number.isFinite(equivalentRadiusMeters) || equivalentRadiusMeters <= 0) {
      throw new Error("Goal landmark radius must be a positive finite number");
    }

    this.equivalentRadiusMeters = equivalentRadiusMeters;
    this.geometry = createLandmarkGeometry();
    this.material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 0.34,
      metalness: 0.42,
      emissive: 0x2a8eb8,
      emissiveIntensity: 0.34,
      flatShading: true,
      transparent: true,
      opacity: 0.96,
      depthTest: true,
      depthWrite: true,
    });
    this.material.name = "tokyo-skytree-daylight-material";

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = "tokyo-skytree-single-draw";
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = 0;

    this.group.name = "tokyo-skytree-goal-landmark";
    this.group.visible = false;
    this.group.add(this.mesh);
  }

  spawn(
    playerXMeters: number,
    playerZMeters: number,
    cameraYaw: number,
    distanceMeters: number,
  ): void {
    if (this.disposed) {
      return;
    }
    const distance = Math.max(0, Number.isFinite(distanceMeters) ? distanceMeters : 0);
    const yaw = Number.isFinite(cameraYaw) ? cameraYaw : 0;
    this.position.set(
      playerXMeters - Math.sin(yaw) * distance,
      0,
      playerZMeters - Math.cos(yaw) * distance,
    );
    this.baseYaw = yaw;
    this.spawned = true;
    this.absorbed = false;
    this.group.visible = false;
  }

  update(
    playerXMeters: number,
    playerZMeters: number,
    renderMetersPerUnit: number,
    elapsed: number,
    visible: boolean,
    absorbProgress: number,
  ): void {
    if (
      this.disposed
      || !this.spawned
      || this.absorbed
      || !Number.isFinite(renderMetersPerUnit)
      || renderMetersPerUnit <= 0
    ) {
      this.group.visible = false;
      return;
    }

    const progress = THREE.MathUtils.clamp(absorbProgress, 0, 1);
    const eased = smoothstep(progress);
    const remaining = 1 - eased;
    const localRadius = this.equivalentRadiusMeters / renderMetersPerUnit;
    const pulse = 1 + Math.sin(elapsed * 2.15) * 0.018 * remaining;
    const shrink = remaining * remaining;
    const localX = (this.position.x - playerXMeters) / renderMetersPerUnit;
    const localZ = (this.position.z - playerZMeters) / renderMetersPerUnit;
    const playerGround = terrainHeightMeters(
      playerXMeters,
      playerZMeters,
      TIERS.length - 1,
    );
    const localY = (this.position.y - playerGround) / renderMetersPerUnit;
    const absorbArc = Math.sin(eased * Math.PI) * localRadius * 0.72;

    this.group.position.set(
      localX * remaining,
      localY * remaining + absorbArc,
      localZ * remaining,
    );
    this.group.scale.setScalar(localRadius * pulse * shrink);
    this.group.rotation.y =
      this.baseYaw
      + elapsed * 0.035
      + Math.sin(elapsed * 0.61) * 0.025;
    this.material.emissiveIntensity =
      0.34
      + Math.sin(elapsed * 2.15) * 0.08
      + eased * 0.92;
    this.group.visible = visible && shrink > 0.0005;
  }

  reset(): void {
    if (this.disposed) {
      return;
    }
    this.spawned = false;
    this.absorbed = false;
    this.position.set(0, 0, 0);
    this.baseYaw = 0;
    this.group.position.set(0, 0, 0);
    this.group.rotation.set(0, 0, 0);
    this.group.scale.set(1, 1, 1);
    this.group.visible = false;
    this.material.emissiveIntensity = 0.34;
  }

  markAbsorbed(): void {
    if (!this.spawned || this.disposed) {
      return;
    }
    this.absorbed = true;
    this.group.visible = false;
  }

  distanceTo(xMeters: number, zMeters: number): number {
    if (!this.spawned || !Number.isFinite(xMeters) || !Number.isFinite(zMeters)) {
      return Number.POSITIVE_INFINITY;
    }
    return Math.hypot(this.position.x - xMeters, this.position.z - zMeters);
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.spawned = false;
    this.absorbed = false;
    this.group.visible = false;
    this.group.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
